import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../pages/home/components/Navbar';
import Footer from '../../pages/home/components/Footer';

interface ServiceDetail {
  icon: string;
  name: string;
  description: string;
  features: string[];
}

interface PricingPlan {
  name: string;
  price: string;
  note: string;
  features: string[];
  highlighted?: boolean;
}

interface FAQ {
  question: string;
  answer: string;
}

interface ServiceDetailLayoutProps {
  // Hero Section
  heroTitle: string;
  heroSubtitle: string;
  heroImage: string;
  breadcrumbs: { name: string; href: string }[];

  // Overview Section
  overviewTitle: string;
  overviewDescription: string;
  overviewImage: string;
  overviewHighlights: string[];

  // Service Details
  serviceDetails: ServiceDetail[];

  // Pricing
  pricingTitle: string;
  pricingPlans: PricingPlan[];

  // FAQ
  faqs: FAQ[];

  // CTA
  ctaTitle: string;
  ctaDescription: string;
}

export default function ServiceDetailLayout({
  heroTitle,
  heroSubtitle,
  heroImage,
  breadcrumbs,
  overviewTitle,
  overviewDescription,
  overviewImage,
  overviewHighlights,
  serviceDetails,
  pricingTitle,
  pricingPlans,
  faqs,
  ctaTitle,
  ctaDescription,
}: ServiceDetailLayoutProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={heroImage}
            alt={heroTitle}
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav className="flex items-center space-x-2 text-sm mb-8">
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && (
                  <i className="ri-arrow-right-s-line text-orange-300 mx-2"></i>
                )}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-orange-400 font-semibold">{crumb.name}</span>
                ) : (
                  <Link
                    to={crumb.href}
                    className="text-white/80 hover:text-white transition-colors cursor-pointer"
                  >
                    {crumb.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Hero Content */}
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {heroTitle}
            </h1>
            <p className="text-xl text-orange-200 leading-relaxed">
              {heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="w-full h-[500px]">
                  <img
                    src={overviewImage}
                    alt={overviewTitle}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2 space-y-6">
              <div>
                <div className="inline-block px-5 py-2 bg-orange-100 rounded-full mb-4">
                  <span className="text-sm font-bold text-orange-600 tracking-wide">服務概覽</span>
                </div>
                <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                  {overviewTitle}
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  {overviewDescription}
                </p>
              </div>

              {/* Highlights */}
              <div className="space-y-3">
                {overviewHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-orange-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-slate-700 text-base leading-relaxed">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-gradient-to-b from-orange-50/40 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">服務內容詳情</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceDetails.map((detail, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-orange-100 hover:shadow-xl hover:border-orange-300 transition-all duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl mb-6">
                  <i className={`${detail.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{detail.name}</h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  {detail.description}
                </p>
                <ul className="space-y-2">
                  {detail.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-orange-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-600 text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{pricingTitle}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mb-6"></div>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              靈活的收費方案，配合不同規模院舍的需求與預算
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-3xl p-8 border-2 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-orange-500 to-orange-600 border-orange-600 shadow-2xl scale-105'
                    : 'bg-white border-orange-200 hover:border-orange-400 hover:shadow-xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="inline-block bg-white/20 text-white text-xs font-bold px-4 py-1 rounded-full">
                      最受歡迎
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3
                    className={`text-2xl font-bold mb-2 ${
                      plan.highlighted ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    {plan.name}
                  </h3>
                  <div className="mb-2">
                    <span
                      className={`text-4xl font-bold ${
                        plan.highlighted ? 'text-white' : 'text-orange-600'
                      }`}
                    >
                      {plan.price}
                    </span>
                  </div>
                  <p
                    className={`text-sm ${
                      plan.highlighted ? 'text-orange-100' : 'text-slate-500'
                    }`}
                  >
                    {plan.note}
                  </p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i
                        className={`ri-checkbox-circle-fill text-lg flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-orange-500'
                        }`}
                      ></i>
                      <span
                        className={`text-sm leading-relaxed ${
                          plan.highlighted ? 'text-white' : 'text-slate-600'
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center font-bold px-6 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    plan.highlighted
                      ? 'bg-white text-orange-600 hover:bg-orange-50'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  立即查詢
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-orange-50/40 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">常見問題</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mb-6"></div>
            <p className="text-slate-600 text-lg">
              院舍客戶最關心的問題，我們為您一一解答
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-orange-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-orange-50/50 transition-colors"
                >
                  <span className="text-lg font-bold text-slate-900 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-orange-500 flex-shrink-0 transition-transform duration-300 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 pt-2">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-500">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-customer-service-2-line text-4xl text-white"></i>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {ctaTitle}
          </h2>
          <p className="text-xl text-orange-100 mb-10 leading-relaxed max-w-2xl mx-auto">
            {ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-white text-orange-600 font-bold px-10 py-5 rounded-full hover:bg-orange-50 transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2 text-xl"></i>
              預約免費諮詢
            </Link>
            <a
              href="tel:24015938"
              className="inline-flex items-center justify-center border-2 border-white text-white font-bold px-10 py-5 rounded-full hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2 text-xl"></i>
              2401 5938
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}